import { CommonModule, isPlatformServer } from '@angular/common';
import {
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  ApplicationConfig,
  importProvidersFrom,
  PLATFORM_ID,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  BrowserModule,
  provideClientHydration,
  withEventReplay,
} from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  PreloadAllModules,
  provideRouter,
  TitleStrategy,
  withComponentInputBinding,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
  withPreloading,
  withRouterConfig,
} from '@angular/router';
import { createFragmentRegistry } from '@apollo/client/cache';
import { ApolloLink, InMemoryCache } from '@apollo/client/core';
import { APOLLO_NAMED_OPTIONS, NamedOptions } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { withScalars } from 'apollo-link-scalars';
import { buildClientSchema } from 'graphql';
import { DateTimeResolver } from 'graphql-scalars';
import { NgxStripeModule } from 'ngx-stripe';
import { MessageService } from 'primeng/api';
import { appRoutes } from './app.routes';
import { environment } from './environments/environment';
import { RcAuthModule } from './modules/auth/auth.module';
import { Fragments } from './modules/rc-api/fragments';
import { introspectionResult } from './modules/rc-api/generated';
import { RcApiModule } from './modules/rc-api/rc-api.module';
import { RC_API_APOLLO_CLIENT_NAME } from './modules/rc-api/services/rc-api.service';
import { RcUiKitModule } from './modules/rc-ui-kit/rc-ui-kit.module';
import { RcTrackingModule } from './modules/tracking/tracking.module';
import { LOCAL_STORAGE } from './services/storage.service';
import { RCTitleStrategy } from './services/title-strategy.service';

export const appConfig: ApplicationConfig = {
  providers: [
    provideClientHydration(withEventReplay()),
    importProvidersFrom(
      BrowserModule,
      CommonModule,
      FormsModule,
      NgxStripeModule.forRoot(environment.stripe.publishableKey),
      RcApiModule,
      RcAuthModule,
      RcTrackingModule,
      RcUiKitModule,
      ReactiveFormsModule,
    ),
    MessageService,
    // Must provide various apollo configs here vs. their more ideal local-module scope due to angular apollo setup
    // Maybe refactor into a general GraphqlModule that provides the individual clients to their respective modules?
    {
      provide: APOLLO_NAMED_OPTIONS,
      useFactory(httpLink: HttpLink): NamedOptions {
        // Map GraphQL scalars to appropriate resolvers here
        const typesMap = {
          DateTime: DateTimeResolver,
        };
        const schema = buildClientSchema(introspectionResult);

        // Used to automatically transform scalars into interal data types based on typesMap above
        const buildLink = (
          opts: Parameters<typeof httpLink.create>[0],
        ): ApolloLink =>
          ApolloLink.from([
            withScalars({ schema, typesMap }),
            httpLink.create(opts),
          ]);

        return {
          // TODO: Remove split named apollo clients, unnecessary since stitching schemas
          [RC_API_APOLLO_CLIENT_NAME]: {
            cache: new InMemoryCache({
              fragments: createFragmentRegistry(Fragments),
            }),
            link: buildLink({
              uri: `${environment.apiUrl}/graphql`,
            }),
            defaultOptions: {
              query: {
                //fetchPolicy: 'network-only',
              },
              watchQuery: {
                refetchWritePolicy: 'overwrite',
              },
              mutate: {
                // Workaround for defaulting all mutations to clear query caching
                update: async (cache): Promise<void> => {
                  cache.evict({ id: 'ROOT_QUERY' });
                  cache.gc();
                },
                awaitRefetchQueries: true,
              },
            },
          },
        };
      },
      deps: [HttpLink],
    },
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    provideAnimations(),
    provideRouter(
      appRoutes,
      withEnabledBlockingInitialNavigation(),
      // Doesn't appear to be working well for standalone components?
      //withPreloading(QuicklinkStrategy),
      withPreloading(PreloadAllModules),
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      }),
      withComponentInputBinding(),
      withRouterConfig({
        paramsInheritanceStrategy: 'always',
      }),
    ),
    { provide: TitleStrategy, useClass: RCTitleStrategy },
    // Setup for localStorage when running in browser
    {
      provide: LOCAL_STORAGE,
      useFactory: (platformId: object) => {
        if (isPlatformServer(platformId)) {
          return {}; // Return an empty object on the server
        }
        return localStorage; // Use the browser's localStorage
      },
      deps: [PLATFORM_ID],
    },
  ],
};
